import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    setStockVault, setVault, unsetStockVault, unsetVault
} from '../vault/vaultSlice';
import {
    setFloor, setStockFloor, unsetFloor, unsetStockFloor
} from '../floor/floorSlice';
import {setLid, unsetLid} from '../lid/lidSlice';
import {
    setIntermediateCeiling, unsetIntermediateCeiling
} from '../intermediateCeiling/intermediateCeilingSlice';
import {planApiActionTypes} from '../../constants';

export enum VaultElementType {
    Vault = 'vault',
    Lid = 'lid',
    IntermediateCeiling = 'intermediateceiling',
    Floor = 'floor',
}

export interface VaultElement {
    elementType: VaultElementType;
    id: string | null;
    includeWireRopeLoops: boolean;
    quantity: number;
}

interface VaultElementsState {
    items: VaultElement[]
}

const initialState: VaultElementsState = {
    items: [{elementType: VaultElementType.Vault, id: null, includeWireRopeLoops: false, quantity: 1}],
};

// add / remove helpers
const addElement = (state: VaultElementsState, type: VaultElementType) => {
    if (!state.items.find(i => i.elementType === type)) {
        state.items.push({elementType: type, id: null, includeWireRopeLoops: false, quantity: 1});
    }
};

const removeElement = (state: VaultElementsState, type: VaultElementType) => {
    state.items = state.items.filter(i => i.elementType !== type);
};

const vaultElementSlice = createSlice({
    name: 'vaultElements',
    initialState,
    reducers: {
        reset: () => {
            return initialState
        },
        setVaultElement: (state, {payload}: PayloadAction<{ elementType: VaultElementType; id: string | null; }>) => {
            const found = state.items.find(i => i.elementType === payload.elementType && i.id === payload.id);

            if (!found) {
                state.items.push({
                    elementType: payload.elementType,
                    id: payload.id,
                    includeWireRopeLoops: false,
                    quantity: 1
                });
            }
        },
        unsetVaultElement: (state, {payload}: PayloadAction<
            {
                elementType: VaultElementType | 'any';
                id: string | null | 'any' | 'nonnull';
            }>) => {
            const remove = (item: VaultElement): boolean => {
                if (
                    payload.elementType !== 'any' &&
                    payload.elementType !== item.elementType
                ) return false;
                if (payload.id === 'any') return true;
                if (payload.id === 'nonnull') return item.id !== null;
                return item.id === payload.id;
            }

            state.items = state.items.filter(i => !(remove(i)));
        },
        setVaultElementQuantity: (
            state,
            {payload}: PayloadAction<{ elementType: VaultElementType; id: string | null; quantity: number }>
        ) => {
            const found = state.items.find(i => i.elementType === payload.elementType && i.id === payload.id);
            if (found) {
                found.quantity = payload.quantity;
            } else {
                state.items.push({
                    elementType: payload.elementType,
                    includeWireRopeLoops: false,
                    id: payload.id,
                    quantity: payload.quantity,
                });
            }
        },
        setIncludeWireRopeLoops: (
            state,
            {payload}: PayloadAction<{
                elementType: VaultElementType;
                id: string | null;
                includeWireRopeLoops: boolean
            }>
        ) => {
            const found = state.items.find(i => i.elementType === payload.elementType && i.id === payload.id);
            if (found) {
                found.includeWireRopeLoops = payload.includeWireRopeLoops;
            } else {
                state.items.push({
                    elementType: payload.elementType,
                    id: payload.id,
                    includeWireRopeLoops: payload.includeWireRopeLoops,
                    quantity: 0,
                });
            }
        },
    },

    extraReducers: builder => {
        builder.addCase(setVault, state => addElement(state, VaultElementType.Vault))
        builder.addCase(unsetVault, state => removeElement(state, VaultElementType.Vault))
        builder.addCase(setStockVault, state => addElement(state, VaultElementType.Vault))
        builder.addCase(unsetStockVault, state => removeElement(state, VaultElementType.Vault))
        builder.addCase(setFloor, state => addElement(state, VaultElementType.Floor))
        builder.addCase(unsetFloor, state => removeElement(state, VaultElementType.Floor))
        builder.addCase(setStockFloor, state => addElement(state, VaultElementType.Floor))
        builder.addCase(unsetStockFloor, state => removeElement(state, VaultElementType.Floor))
        builder.addCase(setLid, state => addElement(state, VaultElementType.Lid))
        builder.addCase(unsetLid, state => removeElement(state, VaultElementType.Lid))
        builder.addCase(
            setIntermediateCeiling,
            state => addElement(state, VaultElementType.IntermediateCeiling)
        )
        builder.addCase(
            unsetIntermediateCeiling,
            state => removeElement(state, VaultElementType.IntermediateCeiling)
        )
        builder.addCase(
            planApiActionTypes.readPlanSuccess,
            (state, action: any) => {
                if (action.data.vault) addElement(state, VaultElementType.Vault)
                if (action.data.floor) addElement(state, VaultElementType.Floor)
                if (action.data.lid) addElement(state, VaultElementType.Lid)
                if (action.data.intermediateCeiling) addElement(state, VaultElementType.IntermediateCeiling)
            }
        )
        builder.addCase(
            'persist/REHYDRATE', (state, action: any) => {
                if (action.payload && action.payload.project?.vaultElement?.items) {
                    return {
                        ...state,
                        items: action.payload.project.vaultElement.items
                    };
                }
                return state;
            }
        )
    },
});

export const {
    reset,
    setVaultElement,
    unsetVaultElement,
    setVaultElementQuantity,
    setIncludeWireRopeLoops
} = vaultElementSlice.actions;
export default vaultElementSlice.reducer;